import { type ReactNode } from 'react';
import './Toggle.css';

interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** The visible label. Also the control's accessible name. */
  label: ReactNode;
  /** Second line under the label — the "what this does" sentence. */
  description?: ReactNode;
  disabled?: boolean;
  /** `sm` for dense grids (permission matrices, option lists). */
  size?: 'md' | 'sm';
  /** Hide the label visually but keep it for screen readers. */
  hideLabel?: boolean;
  className?: string;
  title?: string;
}

/**
 * The on/off control for this app. **Use this instead of
 * `<input type="checkbox">`** — see `frontend-patterns.md` §5.8.
 *
 * It is a `<button role="switch">`, not a styled checkbox, for two reasons:
 * the whole control (track + label) is one click target, and there is no
 * hidden input to keep in sync. `role="switch"` reads as "on"/"off" rather
 * than "checked", which is what this actually means everywhere it's used.
 *
 * The label lives INSIDE the button rather than in a wrapping `<label>`:
 * `<button>` is a labelable element, so an enclosing label would override the
 * accessible name with its own text.
 */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  size = 'md',
  hideLabel = false,
  className,
  title,
}: ToggleProps): JSX.Element {
  const cls = [
    'toggle',
    size === 'sm' ? 'toggle-sm' : '',
    checked ? 'toggle-on' : '',
    className ?? '',
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={cls}
      disabled={disabled}
      title={title}
      onClick={() => onChange(!checked)}
    >
      <span className="toggle-track" aria-hidden>
        <span className="toggle-thumb" />
      </span>
      {/* Inside the button so the whole block is one click target. That does
          fold `description` into the accessible name, which is why there is no
          aria-describedby: pointing it at a descendant would say it twice. */}
      <span className={hideLabel ? 'toggle-label sr-only' : 'toggle-label'}>
        {label}
        {description && <small>{description}</small>}
      </span>
    </button>
  );
}
